/**
 * PDP Main 2026 — Cart / subscription widget
 *
 * Handles the product form: package-size selection, purchase option
 * (Subscribe & Save vs One Time), delivery frequency, quantity, price
 * recalculation and AJAX add-to-cart. Preserves the Smartrr contract by
 * keeping the hidden `id` / `selling_plan` inputs in sync and reading
 * `window.smartrrProductList` for accurate pricing when available.
 *
 * Cleaned rewrite of assets/pdp-cart-widget.js: unique ids, real buttons,
 * shop-currency aware price formatting, single set of package pills.
 */
(function () {
  'use strict';

  /** @param {HTMLFormElement} form */
  class PdpCart2026 {
    /** @param {HTMLFormElement} form */
    constructor(form) {
      this.form = form;
      this.productId = String(form.dataset.productId || form.dataset.smartrrFormId || '');
      this.currency = form.dataset.currency || 'USD';
      this.locale = document.documentElement.lang || 'en-US';

      this.variantInput = form.querySelector('[data-variant-input]');
      this.sellingPlanInput = form.querySelector('[data-selling-plan-input]');
      this.freqSelect = form.querySelector('[data-freq-select]');

      this.isSubscription = true;
      this.selectedVariantId = this.variantInput ? this.variantInput.value : '';
    }

    init() {
      this._setupSizes();
      this._setupPurchaseOptions();
      this._setupFrequency();
      this._setupQuantity();
      this._setupSubmit();
      this._syncInitial();
    }

    /* ---------- Package size pills ---------- */
    _setupSizes() {
      this.sizeInputs = Array.from(this.form.querySelectorAll('input[name="package-size"]'));
      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', () => this._onSizeChange(input));
      });
    }

    /**
     * Show/hide package sizes based on the active purchase mode. While
     * "Subscribe & Save" is selected only subscribable variants are shown
     * (a variant is subscribable when it has a selling plan allocation);
     * One Time Purchase shows every size. If the currently selected size is
     * hidden by the switch, the first visible size is selected instead.
     */
    _applySizeMode() {
      if (!this.sizeInputs || !this.sizeInputs.length) return;
      let firstVisible = null;
      let selectedHidden = false;

      this.sizeInputs.forEach((input) => {
        const label = input.closest('.pdp-main-2026__size');
        if (!label) return;
        const subscribable = input.dataset.subscribable === 'true';
        const hide = this.isSubscription && !subscribable;
        label.classList.toggle('is-mode-hidden', hide);
        if (!hide && !input.disabled && !firstVisible) firstVisible = input;
        if (hide && input.checked) selectedHidden = true;
      });

      if (selectedHidden && firstVisible) {
        firstVisible.checked = true;
        this._onSizeChange(firstVisible);
      }
    }

    /** @param {HTMLInputElement} input */
    _onSizeChange(input) {
      this.form.querySelectorAll('.pdp-main-2026__size').forEach((el) => el.classList.remove('is-selected'));
      const label = input.closest('.pdp-main-2026__size');
      if (label) label.classList.add('is-selected');

      this.selectedVariantId = input.dataset.variantId || input.value;
      if (this.variantInput) this.variantInput.value = this.selectedVariantId;

      this._syncSmartrr();
      this._updatePrices();
      this._updateAtcState();
    }

    /* ---------- Purchase options ---------- */
    _setupPurchaseOptions() {
      this.options = Array.from(this.form.querySelectorAll('.pdp-main-2026__option'));
      this.optionInputs = Array.from(this.form.querySelectorAll('input[name="purchase_option"]'));
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => this._onPurchaseOptionChange(input));
      });
    }

    /** @param {HTMLInputElement} input */
    _onPurchaseOptionChange(input) {
      const option = input.closest('.pdp-main-2026__option');
      this.options.forEach((el) => el.classList.remove('is-active'));
      if (option) option.classList.add('is-active');

      this.isSubscription = !!(option && option.classList.contains('pdp-main-2026__option--sub'));

      if (this.isSubscription) {
        this._applyFrequency();
      } else if (this.sellingPlanInput) {
        this.sellingPlanInput.value = '';
      }

      this._applySizeMode();
      this._syncSmartrr();
      this._updatePrices();
      this._updateAtcState();
    }

    /* ---------- Delivery frequency ---------- */
    _setupFrequency() {
      if (!this.freqSelect) return;
      this.freqSelect.addEventListener('change', () => {
        if (this.isSubscription) this._applyFrequency();
        this._syncSmartrr();
        this._updatePrices();
        this._updateAtcState();
      });
    }

    _applyFrequency() {
      if (!this.freqSelect || !this.sellingPlanInput) return;
      this.sellingPlanInput.value = this.freqSelect.value || '';
    }

    /* ---------- Quantity ---------- */
    _setupQuantity() {
      const input = this.form.querySelector('input[name="quantity"]');
      if (!(input instanceof HTMLInputElement)) return;
      const dec = this.form.querySelector('[data-qty-decrease]');
      const inc = this.form.querySelector('[data-qty-increase]');
      const min = parseInt(input.min || '1', 10) || 1;
      const max = parseInt(input.max || '999', 10) || 999;

      const clamp = () => {
        let val = parseInt(input.value || '1', 10);
        if (isNaN(val)) val = min;
        val = Math.max(min, Math.min(max, val));
        input.value = String(val);
        if (dec instanceof HTMLButtonElement) dec.disabled = val <= min;
        if (inc instanceof HTMLButtonElement) inc.disabled = val >= max;
      };

      if (dec) dec.addEventListener('click', () => { input.value = String((parseInt(input.value, 10) || min) - 1); clamp(); });
      if (inc) inc.addEventListener('click', () => { input.value = String((parseInt(input.value, 10) || min) + 1); clamp(); });
      input.addEventListener('input', clamp);
      clamp();
    }

    /* ---------- Prices ---------- */
    /**
     * Returns the active package-size pill (radio).
     * @returns {HTMLInputElement|null}
     */
    _activeSize() {
      return this.form.querySelector('input[name="package-size"]:checked');
    }

    _updatePrices() {
      const size = this._activeSize();
      if (!size) return;
      const qty = parseInt(size.dataset.variantQty || '1', 10) || 1;

      // Prefer live Smartrr data when present, fall back to data attributes.
      let otPrice = parseInt(size.dataset.price || '0', 10);
      let subPrice = parseInt(size.dataset.priceSub || '0', 10);

      const smartrr = this._smartrrVariant(this.selectedVariantId);
      if (smartrr) {
        if (typeof smartrr.price === 'number') otPrice = smartrr.price;
        const planId = this.freqSelect ? this.freqSelect.value : '';
        const alloc = (smartrr.selling_plan_allocations || []).find(
          (a) => a.selling_plan && String(a.selling_plan.id) === String(planId)
        );
        if (alloc && typeof alloc.price === 'number') subPrice = alloc.price;
      }

      this._setText('[data-ot-total]', this._money(otPrice));
      this._setText('[data-ot-unit]', qty > 1 ? `(${this._money(Math.round(otPrice / qty))}/can)` : '');
      this._setText('[data-sub-total]', this._money(subPrice));
      this._setText('[data-sub-unit]', qty > 1 ? `(${this._money(Math.round(subPrice / qty))}/can)` : '');
    }

    /**
     * @param {string} selector
     * @param {string} text
     */
    _setText(selector, text) {
      const el = this.form.querySelector(selector);
      if (el) el.textContent = text;
    }

    /**
     * @param {string|number} variantId
     * @returns {any|null}
     */
    _smartrrVariant(variantId) {
      const list = typeof window !== 'undefined' && window.smartrrProductList;
      const data = list && this.productId ? list[this.productId] : null;
      if (!data) return null;
      const variants = (data.product && data.product.variants) || data.variants;
      if (!Array.isArray(variants)) return null;
      return variants.find((v) => String(v.id) === String(variantId)) || null;
    }

    /** @param {number} cents */
    _money(cents) {
      try {
        return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    /* ---------- Smartrr sync ---------- */
    _syncSmartrr() {
      const forms = document.querySelectorAll(`[data-smartrr-form-id="${this.productId}"]`);
      forms.forEach((f) => {
        if (f === this.form) return;
        const idInput = f.querySelector('input[name="id"]');
        const spInput = f.querySelector('input[name="selling_plan"]');
        if (idInput && this.variantInput) idInput.value = this.variantInput.value;
        if (spInput) spInput.value = this.isSubscription && this.sellingPlanInput ? this.sellingPlanInput.value : '';
      });
    }

    /* ---------- Add to cart state ---------- */
    _updateAtcState() {
      const hasVariant = !!(this.variantInput && this.variantInput.value);
      const hasPlan = !!(this.sellingPlanInput && this.sellingPlanInput.value);
      const canSubmit = hasVariant && (this.isSubscription ? hasPlan : true);
      const btn = this.form.querySelector('.pdp-main-2026__atc');
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = !canSubmit;
        btn.classList.toggle('is-disabled', !canSubmit);
      }
    }

    /* ---------- Submit ---------- */
    _setupSubmit() {
      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = this.form.querySelector('.pdp-main-2026__atc');
        if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

        const textEl = btn.querySelector('.pdp-main-2026__atc-text');
        const originalText = textEl ? textEl.textContent : '';
        btn.disabled = true;
        if (textEl) textEl.textContent = 'Adding…';

        const formData = new FormData(this.form);
        const url = (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

        try {
          const res = await fetch(url, { method: 'POST', body: formData, headers: { Accept: 'application/json' } });
          const data = await res.json();
          if (data.status && data.status >= 400) {
            throw new Error(data.description || data.message || 'Error adding to cart');
          }
          // Hand off to the theme's GoCart drawer, exactly like the legacy
          // `.js-go-cart-add-to-cart` button did: re-fetch the cart, re-render
          // the drawer (items, subtotal, free-shipping bar, header count) and
          // open it — or the success modal, honouring the ajaxcart popup
          // setting — via GoCart's own handler.
          const goCart = /** @type {any} */ (window).goCart;
          if (goCart && typeof goCart.addItemToCartHandler === 'function') {
            goCart.addItemToCartHandler(data);
          } else if (goCart && typeof goCart.fetchAndOpenCart === 'function') {
            goCart.fetchAndOpenCart();
          }
        } catch (err) {
          if (window.console && console.warn) console.warn('[PDP Main 2026]', err);
          window.alert(err.message || 'Error adding to cart');
        } finally {
          btn.disabled = false;
          if (textEl) textEl.textContent = originalText;
          this._updateAtcState();
        }
      });
    }

    /* ---------- Initial sync ---------- */
    _syncInitial() {
      const active = this.form.querySelector('.pdp-main-2026__option.is-active') ||
        this.form.querySelector('.pdp-main-2026__option');
      this.isSubscription = !!(active && active.classList.contains('pdp-main-2026__option--sub'));
      if (this.isSubscription) this._applyFrequency();
      this._applySizeMode();
      this._syncSmartrr();
      this._updatePrices();
      this._updateAtcState();
    }
  }

  function boot() {
    document.querySelectorAll('.js-pdp-2026-form').forEach((form) => {
      if (form instanceof HTMLFormElement && !form.dataset.pdp2026Init) {
        form.dataset.pdp2026Init = 'true';
        new PdpCart2026(form).init();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
